import { useState, useEffect } from 'react';
import { Truck, Package, Clock, Zap, CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const serviceIcons = {
  local: Package,
  regional: Truck,
  national: Truck,
  express: Zap,
  regular: Truck,
};

const courierColors = {
  godap: 'bg-blue-100 text-blue-700',
  gig_logistics: 'bg-orange-100 text-orange-700',
};

const CourierSelection = ({ 
  originState = 'Lagos', 
  destState, 
  countryCode,
  packageWeight = 1.0,
  selectedRate,
  onSelectRate 
}) => {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchRates = async () => {
      if (!destState || !countryCode) {
        setRates([]);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Fetch from multi-courier endpoint to get both Go-Dap and GIG Logistics rates
        const response = await axios.get(`${API}/courier/rates/all/quick`, {
          params: {
            origin_state: originState,
            dest_state: destState,
            weight_kg: packageWeight,
            country_code: countryCode
          }
        });

        if (response.data.success) {
          setRates(response.data.rates || []);
          // Auto-select the first (cheapest) rate if none selected
          if (!selectedRate && response.data.rates?.length > 0) {
            onSelectRate(response.data.rates[0]);
          }
        }
      } catch (err) {
        console.error('Failed to fetch shipping rates:', err);
        setError('Unable to fetch shipping rates');
        setRates([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRates();
  }, [destState, countryCode, originState, packageWeight]);

  if (!destState || !countryCode) {
    return (
      <div className="text-sm text-muted-foreground text-center py-4">
        Enter shipping address to see delivery options
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-3">
        <Label className="flex items-center gap-2">
          <Truck className="w-4 h-4" />
          Delivery Options
        </Label>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-20 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-sm text-red-500 text-center py-4">
        {error}
      </div>
    );
  }

  if (rates.length === 0) {
    return (
      <div className="text-sm text-muted-foreground text-center py-4">
        No delivery options available for this location
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <Label className="flex items-center gap-2">
        <Truck className="w-4 h-4" />
        Delivery Options
      </Label>
      
      <div className="space-y-2">
        {rates.map((rate, index) => {
          const ServiceIcon = serviceIcons[rate.service_type] || Truck;
          const isSelected = selectedRate?.courier === rate.courier && selectedRate?.service_type === rate.service_type;
          const isCheapest = index === 0;
          const courierColorClass = courierColors[rate.courier] || 'bg-gray-100 text-gray-700';
          
          return (
            <Card
              key={`${rate.courier}-${rate.service_type}`}
              className={`cursor-pointer transition-all hover:border-primary/50 ${
                isSelected ? 'border-primary ring-1 ring-primary bg-primary/5' : ''
              }`}
              onClick={() => onSelectRate(rate)}
              data-testid={`shipping-option-${rate.courier}-${rate.service_type}`}
            >
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                      <ServiceIcon className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="font-medium text-sm flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className={`text-xs ${courierColorClass}`}>
                          {rate.courier_name}
                        </Badge>
                        {rate.service_name || rate.service_type}
                        {rate.service_type === 'express' && (
                          <Badge variant="secondary" className="text-xs bg-orange-100 text-orange-700">
                            Fastest
                          </Badge>
                        )}
                        {isCheapest && (
                          <Badge variant="secondary" className="text-xs bg-green-100 text-green-700">
                            Best Value
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {rate.estimated_days} business days
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">
                      {rate.currency} {rate.rate.toLocaleString()}
                    </span>
                    {isSelected && (
                      <CheckCircle2 className="w-5 h-5 text-primary" />
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {selectedRate?.mock && (
        <p className="text-xs text-muted-foreground text-center">
          Estimated rates. Final amount confirmed at checkout.
        </p>
      )}
    </div>
  );
};

export default CourierSelection;
